import * as z from 'zod';

import { brandedId, FeeId, PaymentBreakdownId, PaymentDueId, PaymentId, PayoffId } from '../BrandedIds';
import { zodJSInstantDate } from '../dates/BrandedDate';
import type { PaymentMethod } from './Payments';
import { Payoff } from './Payoffs';

export const StatementPaymentBreakdownType = z.literal('statement');
export const SuspensePaymentBreakdownType = z.literal('suspense');
export const PrincipalPrepaymentPaymentBreakdownType = z.literal('principalPrepayment');
export const FeePaymentBreakdownType = z.literal('fee');
export const AdvancePaymentBreakdownType = z.literal('advance');
export const PayoffPaymentBreakdownType = z.literal('payoff');
export const ThirdPartyDepositBreakdownType = z.literal('thirdParty');
export const BalanceReallocationBreakdownType = z.literal('balanceReallocation');

export const PaymentBucket = z.enum([
  'principal',
  'interest',
  'escrow',
  'reserve',
  'fees',
  'advance',
  'additionalPrincipal',
  'additionalEscrow',
  'holdback',
  'lossDraft',
  'suspense',
  'futureStatementSuspense',
  'unappliedSuspense',
]);
export type PaymentBucket = z.infer<typeof PaymentBucket>;

export const PaymentBreakdownType = z.union([
  StatementPaymentBreakdownType,
  SuspensePaymentBreakdownType,
  PrincipalPrepaymentPaymentBreakdownType,
  FeePaymentBreakdownType,
  AdvancePaymentBreakdownType,
  PayoffPaymentBreakdownType,
  ThirdPartyDepositBreakdownType,
  BalanceReallocationBreakdownType,
]);
export type PaymentBreakdownType = z.infer<typeof PaymentBreakdownType>;

export const VALID_PAYMENT_BREAKDOWN_EVENTS = ['initiated', 'reallocated', 'voided'] as const;

export type PaymentBreakdownEvent = z.infer<typeof PaymentBreakdownEvent>;
export const PaymentBreakdownEvent = z.preprocess((val) => {
  if (val === 'failedOrCancelled') {
    return 'voided';
  }
  return val;
}, z.enum(VALID_PAYMENT_BREAKDOWN_EVENTS));

const BaseBreakdown = z.object({
  id: PaymentBreakdownId.default(() => brandedId('PaymentBreakdownId')), // TODO migrate & require
  createdAt: zodJSInstantDate.default(new Date('1970-01-01T00:00:00.000Z')), // TODO migrate & no default

  /**
   * The ID of the breakdown that this breakdown cancels out. This breakdown's
   * amounts MUST exactly negate the amounts of the breakdown carrying this ID.
   *
   * Should only be present if this is a `voided` breakdown (but this is pretty
   * annoying to bake into the type system so for now it's just optional).
   */
  voidedBreakdownId: PaymentBreakdownId.optional(),

  /**
   * The event that caused this breakdown to be created.
   * This is used to track the history of the breakdown and to determine if it should be considered for calculations.
   *
   * If it is 'initiated', it means the breakdown was created as part of a payment being initiated.
   * If it is 'voided', it means the breakdown was created as part of a payment being failed or cancelled, or the breakdown was rolled back in order to be reallocated subsequently.
   * If it is 'reallocated', it means the breakdown was created as part of a payment being reallocated.
   */
  event: PaymentBreakdownEvent.default('initiated'), // TODO migrate & no default

  paymentId: PaymentId,
  amount: z.number(),
  expectedPayment: z.number(),
});

const StatementBreakdown = BaseBreakdown.extend({
  type: StatementPaymentBreakdownType,
  paymentDueId: PaymentDueId,
  principal: z.number(),
  interest: z.number(),
  escrow: z.number(),
  additionalPrincipal: z.number(),
  additionalEscrow: z.number(),
  suspense: z.number(), // statement suspense
  futureStatementSuspense: z.number().default(0),
  unappliedSuspense: z.number().default(0),
  reserve: z.number().optional(),
});
export type StatementBreakdown = z.infer<typeof StatementBreakdown>;

const SuspenseBreakdown = BaseBreakdown.extend({
  type: SuspensePaymentBreakdownType,
  futureStatementSuspense: z.number(),
  unappliedSuspense: z.number().default(0),
  paymentDueId: PaymentDueId.optional(),
});
export type SuspenseBreakdown = z.infer<typeof SuspenseBreakdown>;

const PrincipalPrepaymentBreakdown = BaseBreakdown.extend({
  type: PrincipalPrepaymentPaymentBreakdownType,
  additionalPrincipal: z.number(),
  reserve: z.number().optional(),
});
export type PrincipalPrepaymentBreakdown = z.infer<typeof PrincipalPrepaymentBreakdown>;

const FeeBreakdown = BaseBreakdown.extend({
  type: FeePaymentBreakdownType,
  feeId: FeeId,
  fees: z.number(),
  unappliedSuspense: z.number().default(0),
});
export type FeeBreakdown = z.infer<typeof FeeBreakdown>;

const AdvanceBreakdown = BaseBreakdown.extend({
  type: AdvancePaymentBreakdownType,
  feeId: FeeId,
  advance: z.number(),
  advanceInterest: z.number(),
  unappliedSuspense: z.number().default(0),
});
export type AdvanceBreakdown = z.infer<typeof AdvanceBreakdown>;

const PayoffBreakdown = BaseBreakdown.extend({
  type: PayoffPaymentBreakdownType,
  payoffId: PayoffId,
  principal: z.number(),
  interest: z.number(),
  escrow: z.number(),
  suspense: z.number(), // statement suspense
  futureStatementSuspense: z.number().default(0),
  unappliedSuspense: z.number().default(0),
  reserve: z.number(),
  fees: z.number(),
  additionalPrincipal: z.number().optional(),
  additionalEscrow: z.number().optional(),
});
export type PayoffBreakdown = z.infer<typeof PayoffBreakdown>;

const ThirdPartyBreakdown = BaseBreakdown.extend({
  type: ThirdPartyDepositBreakdownType,
  escrow: z.number(),
  holdback: z.number(),
  lossDraft: z.number().optional().default(0),
  /** @deprecated @migrationTodo Use futureStatementSuspense or unappliedSuspense instead. This field is silently ignored by the snapshot calculator. */
  suspense: z.number().optional(),
  unappliedSuspense: z.number().default(0),
  principal: z.number(),
});
export type ThirdPartyBreakdown = z.infer<typeof ThirdPartyBreakdown>;

const BalanceReallocationBreakdown = BaseBreakdown.extend({
  type: BalanceReallocationBreakdownType,
  escrow: z.number(),
  holdback: z.number(),
  lossDraft: z.number().optional().default(0),
  /** @deprecated @migrationTodo Use futureStatementSuspense or unappliedSuspense instead. This field is silently ignored by the snapshot calculator. */
  suspense: z.number().optional(),
  futureStatementSuspense: z.number().default(0),
  unappliedSuspense: z.number().default(0),
  principal: z.number(),
});
export type BalanceReallocationBreakdown = z.infer<typeof BalanceReallocationBreakdown>;

export const PaymentBreakdown = z.discriminatedUnion('type', [
  StatementBreakdown,
  SuspenseBreakdown,
  PrincipalPrepaymentBreakdown,
  FeeBreakdown,
  AdvanceBreakdown,
  PayoffBreakdown,
  ThirdPartyBreakdown,
  BalanceReallocationBreakdown,
]);
export type PaymentBreakdown = z.infer<typeof PaymentBreakdown>;

export const ChargeBreakdown = z.discriminatedUnion('type', [FeeBreakdown, AdvanceBreakdown]);
export type ChargeBreakdown = z.infer<typeof ChargeBreakdown>;

export function isStatementBreakdown(item: PaymentBreakdown): item is StatementBreakdown {
  return item.type === 'statement';
}
export function isSuspenseBreakdown(item: PaymentBreakdown): item is SuspenseBreakdown {
  return item.type === 'suspense';
}
export function isPrincipalPrepaymentBreakdown(item: PaymentBreakdown): item is PrincipalPrepaymentBreakdown {
  return item.type === 'principalPrepayment';
}
export function isFeeBreakdown(item: PaymentBreakdown): item is FeeBreakdown {
  return item.type === 'fee';
}
export function isThirdPartyBreakdown(item: PaymentBreakdown): item is ThirdPartyBreakdown {
  return item.type === 'thirdParty';
}
export function isBalanceReallocationBreakdown(item: PaymentBreakdown): item is BalanceReallocationBreakdown {
  return item.type === 'balanceReallocation';
}

export function isAdvanceBreakdown(item: PaymentBreakdown): item is AdvanceBreakdown {
  return item.type === 'advance';
}

export function isChargeBreakdown(item: PaymentBreakdown): item is ChargeBreakdown {
  return item.type === 'fee' || item.type === 'advance';
}

/**
 * True if `payment` represents a principal curtailment.
 *
 * Recognises two shapes:
 *   1. Legacy / system-synthetic payments with `method === 'principalPrepayment'`
 *      (deprecated for new payments. see `PaymentMethod`).
 *   2. New-model payments with a real payment method (check / wire / ACH / ...)
 *      whose breakdowns include a `principalPrepayment` allocation.
 *
 * Pass the snapshot's full `paymentToPaymentDue` array; the function filters to
 * the breakdowns belonging to this payment by id.
 */
export function isPrincipalCurtailmentPayment(
  payment: { id: PaymentId; method: PaymentMethod },
  breakdowns: readonly PaymentBreakdown[],
): boolean {
  if (payment.method === 'principalPrepayment') return true;
  return breakdowns.some((b) => b.paymentId === payment.id && isPrincipalPrepaymentBreakdown(b));
}

export function isPayoffBreakdown(item: PaymentBreakdown): item is PayoffBreakdown {
  return item.type === 'payoff';
}

export function isFullPayoffBreakdown(item: PaymentBreakdown, payoffs: Payoff[]): item is PayoffBreakdown {
  const fullPayoffIds = payoffs.filter((p) => p.isPartial !== true).map((p) => p.id);
  return item.type === 'payoff' && fullPayoffIds.includes(item.payoffId);
}

export function isPartialPayoffBreakdown(item: PaymentBreakdown, payoffs: Payoff[]): item is PayoffBreakdown {
  const partialPayoffIds = payoffs.filter((p) => p.isPartial === true).map((p) => p.id);
  return item.type === 'payoff' && partialPayoffIds.includes(item.payoffId);
}

export const extractValueFromBreakdown = (breakdown: BreakdownLike, bucket: PaymentBucket): number => {
  const obj = breakdown as Record<string, unknown>;
  const value = obj[bucket];
  return typeof value === 'number' ? value : 0;
};

/**
 * Minimal structural shape needed by {@link getUnvoidedBreakdowns}. Compatible
 * with both the iso `PaymentBreakdown` discriminated union and the equivalent
 * GraphQL fragment shapes (which differ in branded-id types and `__typename`
 * but expose the same fields the algorithm reads).
 */
export type BreakdownLike = {
  id?: PaymentBreakdownId | null;
  event: PaymentBreakdownEvent;
  type: string;
  amount: number;
  paymentDueId?: PaymentDueId | null;
  voidedBreakdownId?: PaymentBreakdownId | null;
};

/**
 * From a raw list of breakdowns, return a list of only those breakdowns that
 * aren't voided by a later breakdown in the list.
 *
 * Generic in `T` so callers can pass iso-shaped or GraphQL-shaped breakdowns
 * — the algorithm only reads `id`, `event`, `type`, `amount`, optional
 * `paymentDueId`, optional `voidedBreakdownId`, and bucket fields, all of
 * which are present on both.
 *
 * Pairing strategy:
 * 1. Voided breakdowns that carry an explicit `voidedBreakdownId` reference
 *    are matched directly to their target by id. This is unambiguous and
 *    cheap.
 * 2. For voids that lack `voidedBreakdownId` (legacy data predating that
 *    field, or test fixtures), fall back to structural matching: the algorithm
 *    pairs a non-voided breakdown with a later voided breakdown whose
 *    type, paymentDueId, amount, and per-bucket values exactly negate it.
 */
export function getUnvoidedBreakdowns<T extends BreakdownLike>(allBreakdowns: Iterable<T>): readonly T[] {
  const breakdowns = Array.from(allBreakdowns);

  // Pass 1: collect ids that have been explicitly voided via voidedBreakdownId.
  const explicitlyVoidedIds = new Set<PaymentBreakdownId>();
  for (const breakdown of breakdowns) {
    if (breakdown.event === 'voided' && breakdown.voidedBreakdownId) {
      explicitlyVoidedIds.add(breakdown.voidedBreakdownId);
    }
  }

  const goodBreakdowns: T[] = [];
  const consumedVoidIndices = new Set<number>();

  breakdowns.forEach((breakdown, i) => {
    if (breakdown.event === 'voided') {
      return;
    }

    // Strategy 1: explicit voidedBreakdownId pairing.
    if (breakdown.id && explicitlyVoidedIds.has(breakdown.id)) {
      return;
    }

    // Strategy 2: structural fallback. Only consider voids that DON'T already
    // carry a voidedBreakdownId — those are accounted for in Strategy 1 and
    // shouldn't double-pair against a structurally-similar breakdown.
    const isVoidOfThisBreakdown = (otherBreakdown: T): boolean => {
      if (
        otherBreakdown.event !== 'voided' ||
        otherBreakdown.voidedBreakdownId ||
        otherBreakdown.type !== breakdown.type ||
        ('paymentDueId' in otherBreakdown &&
          'paymentDueId' in breakdown &&
          otherBreakdown.paymentDueId !== breakdown.paymentDueId) ||
        otherBreakdown.amount !== -1 * breakdown.amount
      ) {
        return false;
      }

      for (const bucket of PaymentBucket.options) {
        const thisValue = extractValueFromBreakdown(breakdown, bucket) ?? 0;
        const otherValue = extractValueFromBreakdown(otherBreakdown, bucket) ?? 0;

        if (thisValue === 0 && otherValue === 0) continue;
        if (thisValue !== -1 * otherValue) return false;
      }

      return true;
    };

    const voidingBreakdownIndex = breakdowns
      .slice(i + 1)
      .findIndex((b, j) => isVoidOfThisBreakdown(b) && !consumedVoidIndices.has(i + j + 1));

    if (voidingBreakdownIndex !== -1) {
      consumedVoidIndices.add(i + voidingBreakdownIndex + 1);
    } else {
      goodBreakdowns.push(breakdown);
    }
  });

  return goodBreakdowns;
}
